import { useEffect, useRef, useState } from 'react';
import { ProfileCompletionService } from '../services/profileCompletionService';
import { PersonProfileCompletionDto } from '../types/profileCompletion';

interface UseProfileCompletionOptions {
    enabled?: boolean;
}

interface UseProfileCompletionResult {
    data: PersonProfileCompletionDto | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
    /** Schedules a refetch after `delayMs` milliseconds. If called again before the delay
     *  expires the previous timer is cancelled — safe to call on every CV save. */
    refetchAfterDelay: (delayMs?: number) => void;
}

export const useProfileCompletion = (personId: number | null | undefined, options: UseProfileCompletionOptions = {}): UseProfileCompletionResult => {
    const { enabled = true } = options;
    const [data, setData] = useState<PersonProfileCompletionDto | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [fetchCounter, setFetchCounter] = useState(0);
    const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const refetch = () => setFetchCounter(c => c + 1);

    const refetchAfterDelay = (delayMs = 2500) => {
        if (delayTimerRef.current) {
            clearTimeout(delayTimerRef.current);
        }
        delayTimerRef.current = setTimeout(() => {
            refetch();
            delayTimerRef.current = null;
        }, delayMs);
    };

    useEffect(() => {
        if (!personId || !enabled) {
            return;
        }

        let cancelled = false;
        const service = new ProfileCompletionService();

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const result = await service.getByPersonId(personId);
                if (!cancelled) {
                    setData(result);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err : new Error('Failed to fetch profile completion'));
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [personId, enabled, fetchCounter]);

    return { data, isLoading, error, refetch, refetchAfterDelay };
};
